import * as AvatarPrimitive from '@radix-ui/react-avatar';
import {
  createContext,
  forwardRef,
  useContext,
  type ComponentPropsWithoutRef,
  type ElementRef,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/utils';

export type AvatarSize =
  | '2xs'
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl';

export type AvatarShape = 'circle' | 'square';

type AvatarContextValue = { size: AvatarSize; shape: AvatarShape };

const AvatarContext = createContext<AvatarContextValue>({
  size: 'md',
  shape: 'circle',
});

const avatarVariants = cva('relative flex shrink-0 overflow-hidden bg-muted', {
  variants: {
    size: {
      '2xs': 'size-5',
      xs: 'size-6',
      sm: 'size-8',
      md: 'size-10',
      lg: 'size-12',
      xl: 'size-14',
      '2xl': 'size-16',
      '3xl': 'size-20',
      '4xl': 'size-24',
      '5xl': 'size-28',
      '6xl': 'size-32',
    },
    shape: {
      circle: 'rounded-full',
      square: '',
    },
  },
  compoundVariants: [
    { shape: 'square', size: '2xs', class: 'rounded-md' },
    { shape: 'square', size: 'xs', class: 'rounded-lg' },
    { shape: 'square', size: 'sm', class: 'rounded-lg' },
    { shape: 'square', size: 'md', class: 'rounded-xl' },
    { shape: 'square', size: 'lg', class: 'rounded-xl' },
    { shape: 'square', size: 'xl', class: 'rounded-xl' },
    { shape: 'square', size: '2xl', class: 'rounded-xl' },
    { shape: 'square', size: '3xl', class: 'rounded-xl' },
    { shape: 'square', size: '4xl', class: 'rounded-xl' },
    { shape: 'square', size: '5xl', class: 'rounded-xl' },
    { shape: 'square', size: '6xl', class: 'rounded-xl' },
  ],
  defaultVariants: {
    size: 'md',
    shape: 'circle',
  },
});

const avatarFallbackVariants = cva(
  'flex size-full items-center justify-center font-normal text-foreground select-none',
  {
    variants: {
      size: {
        '2xs': 'text-xs tracking-[0.12px]',
        xs: 'text-sm leading-5',
        sm: 'text-sm leading-5',
        md: 'text-sm leading-5',
        lg: 'text-sm leading-5',
        xl: 'text-sm leading-5',
        '2xl': 'text-sm leading-5',
        '3xl': 'text-base leading-6',
        '4xl': 'text-lg leading-7',
        '5xl': 'text-xl leading-8 tracking-[-0.6px]',
        '6xl': 'text-xl leading-8 tracking-[-0.6px]',
      },
    },
    defaultVariants: { size: 'md' },
  }
);

export interface AvatarProps
  extends
    ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    VariantProps<typeof avatarVariants> {}

const Avatar = forwardRef<ElementRef<typeof AvatarPrimitive.Root>, AvatarProps>(
  ({ className, size = 'md', shape = 'circle', ...props }, ref) => (
    <AvatarContext.Provider value={{ size: size ?? 'md', shape: shape ?? 'circle' }}>
      <AvatarPrimitive.Root
        ref={ref}
        data-slot="avatar"
        className={cn(avatarVariants({ size, shape }), className)}
        {...props}
      />
    </AvatarContext.Provider>
  )
);
Avatar.displayName = 'Avatar';

const AvatarImage = forwardRef<
  ElementRef<typeof AvatarPrimitive.Image>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn('aspect-square h-full w-full object-cover', className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName ?? 'AvatarImage';

const AvatarFallback = forwardRef<
  ElementRef<typeof AvatarPrimitive.Fallback>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => {
  const { size } = useContext(AvatarContext);
  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      className={cn(avatarFallbackVariants({ size }), className)}
      {...props}
    />
  );
});
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName ?? 'AvatarFallback';

/** Matches Figma Avatar Group spacing (overlap + trailing padding). */
export type AvatarGroupSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg';

const avatarGroupVariants = cva(
  'flex items-center [&>[data-slot=avatar]]:ring-2 [&>[data-slot=avatar]]:ring-background [&>[data-slot=avatar]]:ring-offset-0',
  {
    variants: {
      size: {
        '2xs': 'pr-[7px] [&>[data-slot=avatar]]:-mr-[7px]',
        xs: 'pr-[10px] [&>[data-slot=avatar]]:-mr-[10px]',
        sm: 'pr-[11px] [&>[data-slot=avatar]]:-mr-[11px]',
        md: 'pr-[12px] [&>[data-slot=avatar]]:-mr-[12px]',
        lg: 'pr-[14px] [&>[data-slot=avatar]]:-mr-[14px]',
      },
    },
    defaultVariants: { size: 'xs' },
  }
);

export interface AvatarGroupProps extends ComponentPropsWithoutRef<'div'> {
  size?: AvatarGroupSize;
}

function AvatarGroup({ className, size = 'xs', ...props }: AvatarGroupProps) {
  return <div role="group" className={cn(avatarGroupVariants({ size }), className)} {...props} />;
}
AvatarGroup.displayName = 'AvatarGroup';

export { Avatar, AvatarImage, AvatarFallback, AvatarGroup, avatarVariants, avatarGroupVariants };
